import type { OnboardingEventResultValues } from './constants';
import type { ConversionCompletedEvent, OnboardingEvent, RedirectInitEvent, RedirectSuccessEvent } from './types';
import type { Countly } from '@/types/countly';

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import countly from 'countly-sdk-web';

import { formatElapsedTimeSinceInit, parseUa, timeElapsedFrom } from './utils';

import { Track } from '@/types/countly';
import { formatDurationForAnalytics } from '@/utils/timestamp';

export * from './constants';

export class Analytics {
  private Countly: Countly = countly;

  private static instance: Analytics;

  private enabled = false;

  private onboardingStart = 0;

  private redirectInitTime: null | number = null;

  private redirectSuccessTime: null | number = null;

  private constructor() {
    const appKey = import.meta.env.VITE_COUNTLY_APP_KEY;
    const url = import.meta.env.VITE_COUNTLY_URL;

    // Countly rejects every request with `Missing parameter "app_key" or
    // "device_id"` when app_key is empty. The key is a per-deployment secret and
    // is unset in local/demo, so only enable tracking when it's configured.
    this.enabled = Boolean(appKey && url);
    if (!this.enabled) {
      return;
    }

    this.Countly.app_key = appKey;
    this.Countly.url = url;
    this.Countly.init();
    this.Countly.q.push([Track.SESSIONS]);
  }

  static getInstance(): Analytics {
    if (!this.instance) {
      this.instance = new Analytics();
    }

    return this.instance;
  }

  private addEvent(key: string, segmentation: Record<string, unknown>) {
    if (!this.enabled) {
      return;
    }

    this.Countly.add_event({ key, segmentation });
  }

  private getOnboardingDuration() {
    return formatDurationForAnalytics(performance.now() - this.onboardingStart);
  }

  trackUser(organization: string) {
    this.onboardingStart = performance.now();

    if (!this.enabled) {
      return;
    }

    this.Countly.q.push([Track.USER_DETAILS, { organization }]);
  }

  trackStep(step: string) {
    if (!this.enabled) {
      return;
    }

    this.Countly.q.push([Track.PAGE_VIEW, step]);
  }

  trackOnboarding(result: OnboardingEventResultValues, customer?: string, isMobileRedirectFlow: boolean = false) {
    const segmentation: OnboardingEvent = {
      customer,
      duration: this.getOnboardingDuration(),
      result,
      redirect: isMobileRedirectFlow ? 'YES' : 'NO',
    };

    this.addEvent('onboarding', segmentation);
  }

  redirectInit() {
    this.redirectInitTime = Date.now();

    const { browser, os } = parseUa(navigator.userAgent);

    const segmentation: RedirectInitEvent = {
      browser: browser.name,
      operating_system: os.name,
      device_type: 'desktop',
    };

    this.addEvent('redirect_initialized', segmentation);
  }

  // Confirms the redirect successfully led the user to a mobile device.
  redirectSuccess() {
    this.redirectSuccessTime = Date.now();

    const { os } = parseUa(navigator.userAgent);
    const elapsedTime = timeElapsedFrom(this.redirectInitTime);
    const segmentation: RedirectSuccessEvent = {
      operating_system: os.name,
      device_type: 'mobile',
      time_elapsed_since_init: elapsedTime ? formatElapsedTimeSinceInit(elapsedTime) : null,
    };

    this.addEvent('redirect_success', segmentation);
  }

  conversionCompleted() {
    const elapsedTime = timeElapsedFrom(this.redirectSuccessTime);
    const segmentation: ConversionCompletedEvent = {
      time_elapsed_since_success: elapsedTime ? formatElapsedTimeSinceInit(elapsedTime) : null,
    };

    this.addEvent('conversion_completed', segmentation);
  }
}

export const analytics = Analytics.getInstance();
